import { useState } from 'react';
import {
  PRODUCT_TAXONOMY_PAGE_SIZE_DEFAULT,
  type ProductTaxonomyAttributes,
  type ProductTaxonomyResults
} from 'types/brandProducts';
import useHandleApi from './useHandleApi';

type Pagination = {
  page: number;
  totalPages: number;
  totalItems: number;
  hasNext: boolean;
  hasPrevious: boolean;
};

const getDefaultPagination = (): Pagination => ({
  page: 0,
  totalPages: 1,
  totalItems: 0,
  hasNext: false,
  hasPrevious: false
});

const MIN_TAXONOMY_FILTER_LENGTH_BEFORE_TRIGGER_CALL = 2;
const useProductTaxonomy = () => {
  const [taxonomies, setTaxonomies] = useState<ProductTaxonomyAttributes[]>([]);
  const [taxonomyPagination, setTaxonomyPagination] = useState<Pagination>(getDefaultPagination());
  const [taxonomyLoading, setTaxonomyLoading] = useState(false);
  const [taxonomyFilter, setTaxonomyFilter] = useState('');

  const { handleApi } = useHandleApi();

  // === Taxonomy Pagination Handlers ===
  const fetchTaxonomies = async (page = 1, filter?: string) => {
    if (!filter) {
      setTaxonomies([]); // reset taxonomies when filter is empty
      return;
    }

    if (filter.length <= MIN_TAXONOMY_FILTER_LENGTH_BEFORE_TRIGGER_CALL) {
      return;
    }

    setTaxonomyLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('size', `${PRODUCT_TAXONOMY_PAGE_SIZE_DEFAULT}`);
      params.append('page', `${page}`);
      if (filter) params.append('filter', filter);

      /* TODO(Jenky): Replace with SDK at some point */
      const response = await handleApi({
        endpoint: `/products/taxonomies?${params.toString()}`,
        method: 'GET'
      });
      const { data } = response;
      const result = data as ProductTaxonomyResults;

      const totalPages = Math.max(1, Math.ceil(result.total / PRODUCT_TAXONOMY_PAGE_SIZE_DEFAULT));
      setTaxonomyPagination({
        page,
        totalPages,
        hasNext: page < totalPages,
        hasPrevious: page > 1,
        totalItems: result.total
      });
      if (page === 1) setTaxonomies(result.taxonomies);
      else setTaxonomies(prev => [...prev, ...result.taxonomies]);
    } catch {
      /* */
    }
    setTimeout(() => setTaxonomyLoading(false), 500);
  };

  const onFetchTaxonomiesBatch = (filter?: string) => fetchTaxonomies(1, filter);
  const onNextTaxonomiesBatch = (filter: string = taxonomyFilter) => {
    if (taxonomyPagination.hasNext) fetchTaxonomies(taxonomyPagination.page + 1, filter);
  };
  const onPreviousTaxonomiesBatch = (filter: string = taxonomyFilter) => {
    if (taxonomyPagination.hasPrevious) fetchTaxonomies(taxonomyPagination.page - 1, filter);
  };

  const onTaxonomyFilterChange = (filter: string) => {
    setTaxonomyFilter(filter);
    onFetchTaxonomiesBatch(filter);
  };

  const upsertProductTaxonomy = async (
    products: {
      productId: string;
      taxonomyId: string;
      brandId: string;
    }[]
  ) => {
    setTaxonomyLoading(true);
    await handleApi({
      endpoint: `/products/taxonomies`,
      method: 'PUT',
      data: products
    });
    setTaxonomyLoading(false);
  };

  return {
    taxonomyLoading,
    taxonomies,
    taxonomyPagination,
    onFetchTaxonomiesBatch,
    onNextTaxonomiesBatch,
    onPreviousTaxonomiesBatch,
    upsertProductTaxonomy,
    onTaxonomyFilterChange
  };
};

export default useProductTaxonomy;
